import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { ArrowPathIcon, ArrowTopRightOnSquareIcon, BookOpenIcon, EllipsisHorizontalIcon, TrashIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../../common/Button";
import ListPage, { ListColumn } from "../../common/ListPage";
import { SortState, nextSort } from "../../common/ListViewControls";
import Dropdown from "../../Settings/Dropdown";
import { relativeTime } from "../../common/relativeTime";
import { useLocalStorageState } from "../../../hooks/useLocalStorageState";
import { AgencyDocumentRow, DOC_TYPE_LABELS, STAGE_LABELS, documentsApi } from "./api";
import { useAgencyDocuments } from "./useAgencyDocuments";
import { TagChip } from "./tagColors";
import UploadDocumentModal from "./UploadDocumentModal";
import TagManagerModal from "./TagManagerModal";
import SearchPreview from "./SearchPreview";

type SortKey = "title" | "doc_type" | "page_count" | "status" | "created_at";

const STATUS_OPTIONS = [
  { value: "", label: "Any status" },
  { value: "ready", label: "Ready" },
  { value: "processing", label: "Processing" },
  { value: "failed", label: "Failed" },
];

const statusBucket = (d: AgencyDocumentRow) => (d.status === "completed" ? "ready" : d.status === "failed" ? "failed" : "processing");

// The admin's list of policy/statute documents.
const DocumentsTab: React.FC = () => {
  const navigate = useNavigate();
  const { documents, tags, loading, reload } = useAgencyDocuments();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sort, setSort] = useLocalStorageState<SortState<SortKey>>("agencyDocuments.sort", { key: "created_at", dir: "desc" });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [dropped, setDropped] = useState<File[] | undefined>();
  const [tagsOpen, setTagsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [deleting, setDeleting] = useState<AgencyDocumentRow | null>(null);
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = documents.filter(
      (d) =>
        (!q || d.title.toLowerCase().includes(q) || (d.file_name || "").toLowerCase().includes(q) || d.tags.some((t) => t.name.toLowerCase().includes(q))) &&
        (!typeFilter || d.doc_type === typeFilter) &&
        (!statusFilter || statusBucket(d) === statusFilter)
    );
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av: any = sort.key === "created_at" ? new Date(a.created_at || 0).getTime() : (a as any)[sort.key] ?? "";
      const bv: any = sort.key === "created_at" ? new Date(b.created_at || 0).getTime() : (b as any)[sort.key] ?? "";
      return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
    });
  }, [documents, search, typeFilter, statusFilter, sort]);

  const openUpload = (files?: File[]) => {
    setDropped(files);
    setUploadOpen(true);
  };

  const reprocess = async (d: AgencyDocumentRow) => {
    try {
      await documentsApi.reprocess(d.id);
      toast.success("Reprocessing started");
      reload();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Could not reprocess");
    }
  };

  const view = async (d: AgencyDocumentRow) => {
    try {
      window.open(await documentsApi.viewUrl(d.id), "_blank", "noopener");
    } catch {
      toast.error("Could not open the document");
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await documentsApi.remove(deleting.id);
      toast.success("Document deleted");
      setDeleting(null);
      reload();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Could not delete");
    } finally {
      setBusy(false);
    }
  };

  const columns: ListColumn<AgencyDocumentRow, SortKey>[] = [
    {
      key: "title",
      header: "Title",
      sortKey: "title",
      width: 300,
      minWidth: 160,
      truncate: true,
      render: (d) => (
        <span className="flex items-center gap-2 min-w-0">
          <BookOpenIcon className="size-4 text-base-content/50 shrink-0" />
          <span className="truncate font-medium text-base-content">{d.title}</span>
        </span>
      ),
    },
    {
      key: "doc_type",
      header: "Type",
      sortKey: "doc_type",
      width: 100,
      render: (d) => <span className="text-xs rounded-md px-1.5 py-0.5 bg-base-200 text-base-content/80">{DOC_TYPE_LABELS[d.doc_type]}</span>,
    },
    {
      key: "tags",
      header: "Tags",
      minWidth: 160,
      render: (d) =>
        d.tags.length ? (
          <span className="flex flex-wrap gap-1">
            {d.tags.slice(0, 4).map((t) => (
              <TagChip key={t.id} name={t.name} color={t.color} count={t.sections} title={`${t.sections} section${t.sections === 1 ? "" : "s"}`} />
            ))}
            {d.tags.length > 4 && <span className="text-[11px] text-base-content/50 self-center">+{d.tags.length - 4}</span>}
          </span>
        ) : (
          <span className="text-xs text-base-content/40">{d.status === "completed" ? "No tags" : ""}</span>
        ),
    },
    { key: "page_count", header: "Pages", sortKey: "page_count", width: 80, hideBelow: "md", align: "right", render: (d) => <span className="text-xs text-base-content/70 tabular-nums">{d.page_count ?? ""}</span> },
    {
      key: "status",
      header: "Status",
      sortKey: "status",
      width: 150,
      hideBelow: "md",
      render: (d) =>
        d.status === "failed" ? (
          <span className="text-xs text-red-600 dark:text-red-400" title={d.error_message || ""}>
            Failed{d.error_message ? `: ${d.error_message.slice(0, 50)}` : ""}
          </span>
        ) : d.status === "completed" ? (
          <span className="text-xs text-green-700 dark:text-green-400">Ready</span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
            <span className="size-2 rounded-full bg-amber-500 animate-pulse" />
            {d.status === "pending" && (d.attempts || 0) > 0 ? "retrying" : STAGE_LABELS[d.stage] || d.stage}
          </span>
        ),
    },
    { key: "created_at", header: "Uploaded", sortKey: "created_at", defaultDir: "desc", width: 110, hideBelow: "lg", render: (d) => <span className="text-xs text-base-content/60 whitespace-nowrap">{relativeTime(d.created_at)}</span> },
    {
      key: "actions",
      header: "",
      width: 44,
      align: "right",
      render: (d) => (
        <div className="dropdown dropdown-end" onClick={(e) => e.stopPropagation()}>
          <Button variant="clean" size="xs" className="px-1 hover:bg-base-200" title="Actions" tabIndex={0}>
            <EllipsisHorizontalIcon className="size-4" />
          </Button>
          <ul tabIndex={0} className="dropdown-content menu bg-base-100 rounded-lg z-20 p-1 shadow border border-base-300 w-44 text-sm">
            <li>
              <button onClick={() => view(d)}>
                <ArrowTopRightOnSquareIcon className="size-4" /> View file
              </button>
            </li>
            <li className={cn(d.status === "processing" && "disabled")}>
              <button disabled={d.status === "processing"} onClick={() => reprocess(d)}>
                <ArrowPathIcon className="size-4" /> Reprocess
              </button>
            </li>
            <li>
              <button className="text-red-600 dark:text-red-400" onClick={() => setDeleting(d)}>
                <TrashIcon className="size-4" /> Delete
              </button>
            </li>
          </ul>
        </div>
      ),
    },
  ];

  const isFiltered = Boolean(search.trim() || typeFilter || statusFilter);

  return (
    <ListPage<AgencyDocumentRow, SortKey>
      title="Documents"
      subtitle="Policy manuals, directives, statutes, and ordinances. Reports cite these sections and are checked against them."
      action={
        <Button variant="primary" onClick={() => openUpload()}>
          Upload
        </Button>
      }
      items={filtered}
      getKey={(d) => d.id}
      columns={columns}
      sort={sort}
      onSort={(s) => setSort(s.key === sort.key && s.dir === sort.dir ? nextSort(sort, s.key) : s)}
      onRowClick={(d) => navigate(`/agency/dashboard/documents/${d.id}`)}
      rowDisabled={(d) => d.status !== "completed" && d.status !== "failed"}
      search={{ value: search, onChange: setSearch, placeholder: "Search documents and tags" }}
      filters={
        <>
          <Dropdown dense size="sm" menuAlign="start" options={[{ value: "", label: "Any type" }, ...Object.entries(DOC_TYPE_LABELS).map(([value, label]) => ({ value, label }))]} value={typeFilter} onChange={setTypeFilter} />
          <Dropdown dense size="sm" menuAlign="start" options={STATUS_OPTIONS} value={statusFilter} onChange={setStatusFilter} />
          <Button variant="secondary" size="sm" onClick={() => setTagsOpen(true)}>
            Manage tags
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setSearchOpen(true)}>
            Retrieval preview
          </Button>
        </>
      }
      resizeStorageKey="agency-documents"
      loading={loading && documents.length === 0}
      empty={{
        icon: <BookOpenIcon className="size-10 text-base-content/40" />,
        title: "No documents yet",
        description: "Upload your policy manual, directives, and local ordinances. Every section is tagged so the right ones reach each report.",
        action: (
          <Button variant="primary" onClick={() => openUpload()}>
            Upload a document
          </Button>
        ),
      }}
      isFiltered={isFiltered}
      noMatch="No documents match."
      onFilesDropped={(files) => openUpload(files)}
      dropMessage="Drop PDFs, Word, or text files to upload them"
    >
      <UploadDocumentModal
        open={uploadOpen}
        onClose={() => {
          setUploadOpen(false);
          setDropped(undefined);
        }}
        tags={tags}
        initialFiles={dropped}
        onStarted={() => setTimeout(reload, 800)}
      />
      <TagManagerModal open={tagsOpen} onClose={() => setTagsOpen(false)} tags={tags} onChanged={reload} />
      <SearchPreview open={searchOpen} onClose={() => setSearchOpen(false)} tags={tags} />
      {deleting && (
        <div className="modal modal-open" role="dialog">
          <div className="modal-box max-w-md">
            <h3 className="font-bold text-lg">Delete “{deleting.title}”?</h3>
            <p className="text-sm text-base-content/70 mt-2">
              Its sections, tags, and index are removed and new reports stop citing it. Reports already written keep the sections they cited.
            </p>
            <div className="modal-action">
              <Button variant="outline" onClick={() => setDeleting(null)} disabled={busy}>
                Cancel
              </Button>
              <Button variant="danger" onClick={confirmDelete} loading={busy}>
                Delete
              </Button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => !busy && setDeleting(null)} />
        </div>
      )}
    </ListPage>
  );
};

export default DocumentsTab;
